import React, { useEffect, useRef, useState } from "react";
import { Grid, Button, Box, Paper, CircularProgress, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LoadingFeedback from "../../../components/loadingFeedback";
import ImageUploadEvidence from "../../../components/ImageUploadBtn/imageUploadEvidence";
import LaddaButton from "../../../components/buttons/laddaButton";
import { useForm } from "react-hook-form";
import { callActionApiAsync, getEngineeringActImages, insertEngineeringActImages } from "../../../services/apiService";
import { compressEvidenceImage, validateEvidenceBatch } from "../../../utils/compressEvidenceImage";
import ToastNotification from "../../../components/toastNotification";
import VerificationModal from "../../../components/modals/verificationModal";
import { useDispatch } from "react-redux";
import { setEngineeringReportId } from "../../../redux/slices/serviceOrderSlice";

let nextRowId = 0;
const emptyImage = () => ({ rowId: `local-${++nextRowId}`, file: null, preview: null, isNew: true });
const withMinimumImageRows = (images) => {
  const updated = [...images];
  while (updated.length < 2) updated.push(emptyImage());
  return updated;
};
const persistedImage = (image) => ({ ...image, rowId: `saved-${image.id}`, file: null, preview: image.path, isNew: false });
const createRequestId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (value) => value.toString(16).padStart(2, "0")).join("");
const errorMessage = (error) => error?.response?.data?.error || error?.response?.data?.message || error?.message || "No se pudo completar la operación.";

// Compare bytes, not names. Read small chunks to limit extra memory on phones.
const hasDuplicateFiles = async (selected, confirmedFiles) => {
  const filesMatch = async (file, previous) => {
    for (let offset = 0; offset < file.size; offset += 256 * 1024) {
      const [currentBuffer, previousBuffer] = await Promise.all([
        file.slice(offset, offset + 256 * 1024).arrayBuffer(),
        previous.slice(offset, offset + 256 * 1024).arrayBuffer()
      ]);
      const currentBytes = new Uint8Array(currentBuffer);
      const previousBytes = new Uint8Array(previousBuffer);
      if (!currentBytes.every((value, index) => value === previousBytes[index])) return false;
    }
    return true;
  };
  const previousFiles = [...confirmedFiles];
  for (const image of selected) {
    for (const previous of previousFiles) {
      if (image.file.size !== previous.size) continue;
      if (await filesMatch(image.file, previous)) return true;
    }
    previousFiles.push(image.file);
  }
  return false;
};

// The shared callback helper consumes errors. Make the result explicit locally.
const evidenceRequest = async (request) => {
  let succeeded = false;
  let result;
  let failure;
  await callActionApiAsync(
    () => request().catch((error) => { failure = error; throw error; }),
    (data) => { succeeded = true; result = data; }
  );
  if (!succeeded) throw failure || new Error("No se recibió confirmación del servidor.");
  return result;
};

const EvidencesSection = ({ userId, isPageBusy, startProtectedAction, endProtectedAction, engineeringReportId, serviceOrderId, isComplete, dataER, onImageCountChange, onPendingChangesChange }) => {
  const dispatch = useDispatch();
  const [imageToDeleteId, setImageToDeleteId] = useState(null);
  const [savingEvidences, setSavingEvidences] = useState(false);
  const [deletingEvidence, setDeletingEvidence] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [processingImage, setProcessingImage] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const saveLockRef = useRef(false);
  const removalRef = useRef(null);
  const pendingUploadRef = useRef(null);
  const uploadSessionRef = useRef(null);
  if (!uploadSessionRef.current) uploadSessionRef.current = createRequestId();
  const confirmedFilesRef = useRef(new Map());
  const loadedActIdRef = useRef(null);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({ severity: "success", summary: "", detail: "" });
  const [images, setImages] = useState(() => [emptyImage(), emptyImage()]);
  const imagesRef = useRef(images);
  const { control, register, handleSubmit, setValue, getValues } = useForm({ defaultValues: { descriptions: ["", ""] } });

  const notify = (severity, summary, detail) => {
    setToastConfig({ severity, summary, detail });
    setShowToast(true);
  };
  const releasePreview = (image) => {
    if (image.preview?.startsWith("blob:")) URL.revokeObjectURL(image.preview);
  };
  const replaceImages = (updated) => {
    imagesRef.current.forEach((image) => {
      if (!updated.some((item) => item.preview === image.preview)) releasePreview(image);
    });
    imagesRef.current = updated;
    setImages(updated);
  };
  const removeLocalRow = (rowId, selectionOnly) => {
    const currentImages = imagesRef.current;
    const index = currentImages.findIndex((image) => image.rowId === rowId);
    if (index < 0 || currentImages[index].id) return;
    const descriptions = [...(getValues("descriptions") || [])];
    let updated;
    if (selectionOnly) {
      updated = currentImages.map((image) => image.rowId === rowId ? { ...image, file: null, preview: null } : image);
      descriptions[index] = "";
    } else {
      updated = withMinimumImageRows(currentImages.filter((image) => image.rowId !== rowId));
      descriptions.splice(index, 1);
      while (descriptions.length < updated.length) descriptions.push("");
    }
    replaceImages(updated);
    setValue("descriptions", descriptions);
  };

  const handleRemoveImage = (rowId, selectionOnly = false) => {
    if (isComplete || loadingImages || saveLockRef.current) return;
    const image = imagesRef.current.find((item) => item.rowId === rowId);
    if (!image || image.id || (!image.file && imagesRef.current.length <= 2)) return;
    if (!startProtectedAction("removeEvidence")) return;
    removalRef.current = { rowId, selectionOnly };
    setImageToDeleteId(rowId);
  };
  const closeDelete = () => {
    if (saveLockRef.current || !removalRef.current) return;
    removalRef.current = null;
    setImageToDeleteId(null);
    endProtectedAction();
  };
  const handleRemoveImageConfirmed = async () => {
    if (saveLockRef.current || !removalRef.current) return;
    saveLockRef.current = true;
    const removal = removalRef.current;
    removalRef.current = null;
    setDeletingEvidence(true);
    try {
      if (isComplete) return;
      removeLocalRow(removal.rowId, removal.selectionOnly);
    } finally {
      setImageToDeleteId(null);
      setDeletingEvidence(false);
      saveLockRef.current = false;
      endProtectedAction();
    }
  };

  const handleImageChange = async (rowId, file) => {
    if (!file) {
      handleRemoveImage(rowId, true);
      return;
    }
    if (isComplete || loadingImages || saveLockRef.current) return;
    if (!startProtectedAction("processingEvidence")) return;
    saveLockRef.current = true;
    setProcessingImage(true);
    try {
      const current = imagesRef.current.find((image) => image.rowId === rowId);
      if (!current || current.id) return;
      setImages((previous) => previous.map((image) => image.rowId === rowId ? { ...image, processing: true } : image));
      const processed = await compressEvidenceImage(file);
      const preview = URL.createObjectURL(processed);
      replaceImages(imagesRef.current.map((image) => image.rowId === rowId ? { ...image, file: processed, preview } : image));
    } catch (error) {
      setImages((previous) => previous.map((image) => ({ ...image, processing: false })));
      notify("error", "No se pudo preparar la imagen", errorMessage(error));
    } finally {
      setProcessingImage(false);
      saveLockRef.current = false;
      endProtectedAction();
    }
  };
  const addImageInput = () => {
    if (isComplete || loadingImages || saveLockRef.current) return;
    if (!startProtectedAction("addEvidence")) return;
    if (images.length < 20) {
      replaceImages([...images, emptyImage()]);
      setValue("descriptions", [...(getValues("descriptions") || []), ""]);
    }
    endProtectedAction();
  };

  const loadImages = async (actId) => {
    setLoadingImages(true);
    try {
      const data = await evidenceRequest(() => getEngineeringActImages(actId, Date.now()));
      if (!Array.isArray(data)) throw new Error("Respuesta de evidencias inválida.");
      const updated = withMinimumImageRows(data.map(persistedImage));
      const currentIds = new Set(updated.map((image) => Number(image.id)));
      confirmedFilesRef.current.forEach((_, id) => {
        if (!currentIds.has(id)) confirmedFilesRef.current.delete(id);
      });
      replaceImages(updated);
      setValue("descriptions", updated.map((image) => image.description || ""));
      loadedActIdRef.current = Number(actId);
      setLoadFailed(false);
    } finally {
      setLoadingImages(false);
    }
  };

  const onSubmit = async ({ descriptions }) => {
    // Remove empty gaps and move each description together with its own image.
    const compacted = withMinimumImageRows(imagesRef.current.map((image, index) => ({
      ...image, description: image.id ? image.description || "" : descriptions[index] || ""
    })).filter((image) => image.id || image.file));
    replaceImages(compacted);
    setValue("descriptions", compacted.map((image) => image.description || ""));
    const selected = compacted.filter((image) => image.file && !image.id);
    if (!selected.length && loadFailed && engineeringReportId) {
      await loadImages(engineeringReportId);
      setSaveFailed(false);
      return;
    }
    const previous = pendingUploadRef.current;
    const unchanged = previous && previous.rows.length === selected.length && previous.rows.every((row, index) =>
      row.rowId === selected[index].rowId && row.file === selected[index].file && row.description === selected[index].description);
    if (!unchanged) {
      if (!selected.length) {
        notify("warn", "Sin imágenes pendientes", "Selecciona una imagen antes de guardar evidencias.");
        return;
      }
      validateEvidenceBatch(selected.map((image) => image.file));
      if (await hasDuplicateFiles(selected, confirmedFilesRef.current.values())) {
        notify("warn", "Imagen repetida", "Esta imagen está repetida o ya se guardó. Quita la copia para continuar.");
        return;
      }
      const formData = new FormData();
      formData.append("requestId", createRequestId());
      formData.append("userId", userId);
      formData.append("uploadSessionId", uploadSessionRef.current);
      formData.append("engineeringActId", engineeringReportId || 0);
      formData.append("serviceOrderId", serviceOrderId || 0);
      formData.append("made_by_user_id", dataER?.made_by_user_id || dataER?.engineer_id || 0);
      selected.forEach((image) => {
        formData.append("images", image.file);
        formData.append("descriptions", image.description);
      });
      // A retry must preserve ALL data, including the URL and compressed files.
      pendingUploadRef.current = { formData, actId: engineeringReportId, count: selected.length, rows: selected,
        baselineIds: new Set(compacted.filter((image) => image.id).map((image) => Number(image.id))), files: selected.map((image) => image.file) };
    }
    const pending = pendingUploadRef.current;
    try {
      let response = await evidenceRequest(() => insertEngineeringActImages(pending.formData, pending.actId));
      // Older deployments return the report ID without image metadata. Verify the actual list.
      if (response?.engineeringActId && !Array.isArray(response.images)) {
        const savedImages = await evidenceRequest(() => getEngineeringActImages(response.engineeringActId, Date.now()));
        if (Array.isArray(savedImages)) response = { ...response, images: savedImages.filter((image) => !pending.baselineIds.has(Number(image.id))) };
      }
      if (!response?.engineeringActId || !Array.isArray(response.images) || response.images.length !== pending.count) {
        throw new Error("No se pudo confirmar el guardado. Intenta de nuevo.");
      }
      // The POST confirms the inserted IDs. Never resend them if the GET fails.
      response.images.forEach((image, index) => confirmedFilesRef.current.set(Number(image.id), pending.files[index]));
      const uniqueImages = new Map([...imagesRef.current.filter((image) => image.id), ...response.images.map(persistedImage)].map((image) => [Number(image.id), image]));
      const saved = withMinimumImageRows([...uniqueImages.values()]);
      replaceImages(saved);
      setValue("descriptions", saved.map((image) => image.description || ""));
      pendingUploadRef.current = null;
      setSaveFailed(false);
      loadedActIdRef.current = Number(response.engineeringActId);
      dispatch(setEngineeringReportId(response.engineeringActId));
      try {
        await loadImages(response.engineeringActId);
      } catch (error) {
        notify("warn", "Evidencias guardadas", "No se pudo actualizar la vista. Las imágenes ya están guardadas.");
      }
    } catch (error) {
      if (error?.response?.status >= 400) pendingUploadRef.current = null;
      setSaveFailed(true);
      notify("error", "No se pudieron guardar las imágenes", "Puedes editar la selección o pulsar Reintentar.");
    }
  };
  const handleEvidenceSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (saveLockRef.current || loadingImages || isComplete) return;
    if (!startProtectedAction("evidences")) return;
    saveLockRef.current = true;
    setSavingEvidences(true);
    try {
      // RHF omits disabled inputs; preserve descriptions before the busy render.
      const descriptions = [...(getValues("descriptions") || [])];
      await handleSubmit(() => onSubmit({ descriptions }))(event);
    } catch (error) {
      setSaveFailed(true);
      notify("error", "Error al preparar evidencias", errorMessage(error));
    } finally {
      saveLockRef.current = false;
      setSavingEvidences(false);
      endProtectedAction();
    }
  };

  useEffect(() => {
    if (!engineeringReportId || loadedActIdRef.current === Number(engineeringReportId)) return;
    if (!startProtectedAction("loadingEvidences")) return;
    loadImages(engineeringReportId).catch((error) => {
      setLoadFailed(true);
      setSaveFailed(true);
      notify("error", "No se pudieron cargar las evidencias", "Intenta de nuevo.");
    }).finally(endProtectedAction);
    // Only a change of report should load the initial list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineeringReportId]);
  useEffect(() => {
    onImageCountChange?.(images.filter((image) => image.id).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);
  useEffect(() => () => imagesRef.current.forEach(releasePreview), []);
  useEffect(() => {
    // Unsaved selections must trigger the leave-page warning of the parent report.
    onPendingChangesChange?.(images.some((image) => !image.id && image.file));
  }, [images, onPendingChangesChange]);

  const isLoading = loadingImages || savingEvidences || processingImage || deletingEvidence || isPageBusy;
  const editingDisabled = isLoading || isComplete;
  return (
    <Box sx={{ mt: 1 }}>
      <LoadingFeedback loading={loadingImages}>
      <form onSubmit={handleEvidenceSubmit}>
        <Grid container spacing={3}>
          {images.map((image, index) => (
            <Grid item xs={12} md={6} key={image.rowId}>
              <Paper elevation={0} sx={{ position: "relative", p: 2, pt: !isComplete && !image.id && (image.file || images.length > 2) ? 6 : 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
                <ImageUploadEvidence image={image} onImageChange={(file) => handleImageChange(image.rowId, file)}
                  control={control} register={register} index={index} disabled={editingDisabled} uploading={savingEvidences && !!image.file} />
                {!isComplete && !image.id && (image.file || images.length > 2) && <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                  <LaddaButton type="button" width={32} height={32} margin="0" borderRadius="50%"
                    backgroundColor="white" color="#d32f2f" borderColor="transparent"
                    title={image.file ? "Quitar imagen" : "Quitar campo"}
                    aria-label={image.file ? "Quitar imagen" : "Quitar campo"}
                    loading={deletingEvidence && imageToDeleteId === image.rowId}
                    disabled={editingDisabled} onClick={() => handleRemoveImage(image.rowId)}>
                    <CloseIcon fontSize="small" />
                  </LaddaButton>
                </Box>}
              </Paper>
            </Grid>
          ))}
        </Grid>
        {!isComplete && <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <LaddaButton type="button" backgroundColor="white" color="#6A6AD8" borderColor="#6A6AD8"
            onClick={addImageInput} disabled={editingDisabled || images.length >= 20}>Agregar evidencia</LaddaButton>
        </Box>}
        {!isComplete && <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <LaddaButton type="submit" variant="contained" disabled={isLoading} loading={savingEvidences}>
            {saveFailed ? "Reintentar" : "Guardar evidencias"}
          </LaddaButton>
        </Box>}
      </form>
      </LoadingFeedback>
      <ToastNotification {...toastConfig} showToast={showToast} setShowToast={setShowToast} />
      <VerificationModal open={imageToDeleteId !== null} handleClose={closeDelete} loading={deletingEvidence}
        alertText="¿Confirmas quitar esta selección o campo sin guardar? Las imágenes guardadas no se eliminan." onConfirm={handleRemoveImageConfirmed} />
    </Box>
  );
};

export default EvidencesSection;
